// Accounts, from the browser's side: claiming a name with a password, logging in, staying signed in, and guests.
// The game server keeps the accounts (server/accounts.ts); this asks it through POST /api/account.
import { session } from './session.ts';

/** The same name rules the server uses (checked here first, so a typo is caught before asking it). */
export const NAME_RULES = { min: 3, max: 16, passwordMin: 4 };
export const GUEST_NAME = /^player\d+$/i;

export function nameProblem(name: string): string | null {
  const n = name.trim();
  if (!n) return 'Type a player name first. It is shown in lobbies and in game.';
  if (n.length < NAME_RULES.min) return `A name needs at least ${NAME_RULES.min} characters.`;
  if (n.length > NAME_RULES.max) return `A name can be at most ${NAME_RULES.max} characters.`;
  if (!/^[A-Za-z0-9]+$/.test(n)) return 'A name can only use letters and numbers (no spaces or symbols).';
  if (GUEST_NAME.test(n)) return '“Player” and numbers is how guests are named. Choose another name, or play as a guest.';
  return null;
}

type Reply = { ok: true; signed?: { id: string; name: string; key: string }; taken?: boolean; name?: string } | { ok: false; error: string; offline?: true };

/** Asks the game server. With no server to ask (the page opened with `npm run dev`), the answer says offline. */
async function ask(body: Record<string, unknown>): Promise<Reply> {
  try {
    const res = await fetch('/api/account', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
    // (only the game server answers in JSON: anything else means the page was opened without it)
    if (!res.headers.get('content-type')?.includes('json')) return { ok: false, error: 'No game server here.', offline: true };
    return (await res.json()) as Reply;
  } catch {
    return { ok: false, error: 'The game server can’t be reached right now.', offline: true };
  }
}

const KEY = 'arellix.account';
function remember(signed: { id: string; name: string; key: string } | null) {
  try {
    if (signed) localStorage.setItem(KEY, JSON.stringify(signed));
    else localStorage.removeItem(KEY);
  } catch {
    // no storage here (a private window): the sign-in lasts until the page is closed
  }
}
function signIn(signed: { id: string; name: string; key: string }, keep: boolean) {
  session.account = signed;
  session.playerName = signed.name;
  session.guest = false;
  remember(keep ? signed : null);
}

/** As the page opens: still signed in from last time on this browser? */
export async function resumeAccount(): Promise<void> {
  let saved: { id: string; name: string; key: string } | null = null;
  try {
    saved = JSON.parse(localStorage.getItem(KEY) ?? 'null');
  } catch {
    return;
  }
  if (!saved?.key) return;
  // signed in until the server says otherwise (it may simply not be reachable for a moment)
  session.account = saved;
  session.playerName = saved.name;
  const r = await ask({ action: 'resume', key: saved.key });
  if (r.ok && r.signed) signIn(r.signed, true);
  else if (!r.ok && !r.offline) signOutHere();
}

/** Is this name free, taken, or not allowed? (offline: no server to ask) */
export async function checkName(name: string) {
  return ask({ action: 'check', name: name.trim() });
}
export async function claimName(name: string, password: string, keep: boolean) {
  const r = await ask({ action: 'register', name: name.trim(), password });
  if (r.ok && r.signed) signIn(r.signed, keep);
  return r;
}
export async function logIn(name: string, password: string, keep: boolean) {
  const r = await ask({ action: 'login', name: name.trim(), password });
  if (r.ok && r.signed) signIn(r.signed, keep);
  return r;
}
function signOutHere() {
  session.account = null;
  session.playerName = '';
  session.guest = false;
  remember(null);
}
export async function logOut() {
  const key = session.account?.key;
  signOutHere();
  if (key) await ask({ action: 'logout', key });
}

/** Playing as a guest: "Player" and four random numbers. */
export function playAsGuest() {
  session.account = null;
  session.guest = true;
  session.playerName = `Player${Math.floor(1000 + Math.random() * 9000)}`;
}

/** No game server (the page from `npm run dev`): play under the name typed, unchecked, as before accounts. */
export function playOffline(name: string) {
  session.account = null;
  session.guest = false;
  session.playerName = name.trim();
}
